import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Button from './Button';

interface AccordionItemProps {
  title: string;
  headingLevel?: 2 | 3;
  subtitle?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  headingLevel = 3,
  subtitle,
  badge,
  children,
  defaultOpen = false,
  onToggle,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const Heading = headingLevel === 2 ? 'h2' : 'h3';

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  return (
    <div className="border border-border-default rounded-card overflow-hidden bg-surface-primary shadow-card">
      <Heading aria-label={title}>
        <Button
          type="button"
          variant="ghost"
          fullWidth
          aria-expanded={isOpen}
          onClick={handleToggle}
        >
          <span className="flex items-center gap-3 flex-1">
            <span className="flex-1 text-left whitespace-normal">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold text-text-primary">{title}</span>
                {badge && <span>{badge}</span>}
              </span>
              {subtitle && (
                <span className="block text-sm text-text-muted mt-0.5">{subtitle}</span>
              )}
            </span>
          </span>
          <span className="ml-3">
            {isOpen ? (
              <ChevronUp size={20} className="text-text-muted" />
            ) : (
              <ChevronDown size={20} className="text-text-muted" />
            )}
          </span>
        </Button>
      </Heading>
      
      {isOpen && (
        <div className="border-t border-border-default bg-surface-primary">
          <div className="p-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

interface AccordionProps {
  children: React.ReactNode;
  className?: string;
}

export const Accordion: React.FC<AccordionProps> = ({ children, className = '' }) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {children}
    </div>
  );
};

export default Accordion;
